import React, { Component } from 'react';
import moment from 'moment';

/* ── helpers ─────────────────────────────────────────────── */
const val = (v, fallback = '—') => (v !== undefined && v !== null && v !== '') ? v : fallback;
const arr = (a) => (Array.isArray(a) && a.length > 0) ? a : null;

const ReviewCard = ({ title, stepIndex, onGoToStep, children }) => (
	<div className="rfp-review-card">
		<div className="rfp-review-card-header">
			<h3 className="rfp-review-card-title">{title}</h3>
			{onGoToStep && (
				<button
					type="button"
					className="rfp-review-edit-btn"
					onClick={() => onGoToStep(stepIndex)}
				>
					<i className="las la-pencil-alt" />
					Edit
				</button>
			)}
		</div>
		<div className="rfp-review-card-body">{children}</div>
	</div>
);

const Field = ({ label, value }) => (
	<div className="rfp-review-field">
		<span className="rfp-review-label">{label}</span>
		<span className="rfp-review-value">{val(value)}</span>
	</div>
);

const Chips = ({ items }) => (
	<div className="rfp-review-chips">
		{(items || []).map((item, i) => (
			<span key={i} className="rfp-review-chip">{item}</span>
		))}
	</div>
);

class ReviewSubmitStep extends Component {
	render() {
		const { data, onGoToStep, teamMembers } = this.props;

		/* ── Event Details ── */
		const eventSection = (
			<ReviewCard title="Event Details" stepIndex={0} onGoToStep={onGoToStep}>
				<Field label="Event Name" value={data.eventName} />
				<Field label="Event Type" value={data.eventType} />
				<Field label="Peak Attendees" value={data.expectedPeakAttendees} />
				<Field label="Max Room Rate" value={data.maxRoomRate ? `$${data.maxRoomRate}` : null} />
				<Field label="Hotel Star Rating" value={data.hotelStarRating ? `${data.hotelStarRating} ★` : null} />
				<Field label="Desired Brands" value={data.desiredBrands} />
			</ReviewCard>
		);

		/* ── Locations ── */
		const locs = data.locations || {};
		const locationSection = (
			<ReviewCard title="Locations" stepIndex={1} onGoToStep={onGoToStep}>
				{arr(locs.regions) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Regions</span>
						<Chips items={locs.regions} />
					</div>
				) : <Field label="Regions" value={null} />}
				{arr(locs.countries) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Countries</span>
						<Chips items={locs.countries} />
					</div>
				) : null}
				{arr(locs.cities) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Cities</span>
						<Chips items={locs.cities} />
					</div>
				) : null}
				{arr(locs.hotels) ? (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Hotels</span>
						<Chips items={locs.hotels} />
					</div>
				) : null}
			</ReviewCard>
		);

		/* ── Preferred Dates ── */
		const fmt = (d) => d ? moment(d).format('MMM D, YYYY') : null;
		const datesSection = (
			<ReviewCard title="Preferred Dates" stepIndex={2} onGoToStep={onGoToStep}>
				<Field label="Arrival" value={fmt(data.preferredArrivalDate)} />
				<Field label="Departure" value={fmt(data.preferredDepartureDate)} />
				{arr(data.alternativeDates) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Alt. Dates</span>
						<div className="rfp-review-value-col">
							{data.alternativeDates.map((d, i) => (
								<span key={i}>
									{fmt(d.arrivalDate)} – {fmt(d.departureDate)}
								</span>
							))}
						</div>
					</div>
				)}
				{arr(data.programDays) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Program Days</span>
						<Chips items={data.programDays} />
					</div>
				)}
				{data.openToAltArrival && arr(data.altArrivalDays) && (
					<div className="rfp-review-row">
						<span className="rfp-review-label">Alt. Arrival Days</span>
						<Chips items={data.altArrivalDays} />
					</div>
				)}
				{arr(data.roomBlock) && (
					<div className="rfp-review-row" style={{ marginTop: 8 }}>
						<span className="rfp-review-label">Room Block</span>
						<div className="rfp-review-rb-table">
							<div className="rfp-review-rb-head">
								<span>Date</span><span>King</span><span>Dbl</span><span>Suite</span><span>Total</span>
							</div>
							{data.roomBlock.map((row, i) => (
								<div key={i} className="rfp-review-rb-row">
									<span>{fmt(row.date)}</span>
									<span>{row.king || 0}</span>
									<span>{row.double || 0}</span>
									<span>{row.suite || 0}</span>
									<span><strong>{row.total || 0}</strong></span>
								</div>
							))}
						</div>
					</div>
				)}
				{data.datePatternNotes && <Field label="Pattern Notes" value={data.datePatternNotes} />}
			</ReviewCard>
		);

		/* ── Meeting Space ── */
		const agendaSection = (
			<ReviewCard title="Meeting Space &amp; Agenda" stepIndex={3} onGoToStep={onGoToStep}>
				{arr(data.agendaItems) ? (
					data.agendaItems.map((item, i) => (
						<div key={i} className="rfp-review-agenda-row">
							<span className="rfp-review-agenda-time">
								{item.startTime} – {item.endTime}
							</span>
							<span className="rfp-review-agenda-type">{item.roomType}</span>
							{item.attendees > 0 && (
								<span className="rfp-review-meta">
									<i className="las la-user" /> {item.attendees}
								</span>
							)}
							{arr(item.setupPreference) && (
								<span className="rfp-review-meta">
									<i className="las la-th" /> {item.setupPreference[0]}
								</span>
							)}
							{item.sessionName && (
								<span className="rfp-review-meta rfp-review-italic">{item.sessionName}</span>
							)}
							{item.require24Hour && (
								<span className="rfp-review-badge">24hr</span>
							)}
						</div>
					))
				) : (
					<span className="rfp-review-empty">No agenda items added.</span>
				)}
			</ReviewCard>
		);

		/* ── Concessions ── */
		const allConcessions = [
			...(data.selectedAutoItems || []),
			...(data.customConcessions || [])
		];
		const concessionsSection = (
			<ReviewCard title="Concessions" stepIndex={4} onGoToStep={onGoToStep}>
				{arr(allConcessions) ? (
					<Chips items={allConcessions} />
				) : (
					<span className="rfp-review-empty">No concessions specified.</span>
				)}
			</ReviewCard>
		);

		/* ── Key Considerations ── */
		const considerationsSection = (
			<ReviewCard title="Key Considerations" stepIndex={5} onGoToStep={onGoToStep}>
				{data.decisionFactors && <Field label="Decision Factors" value={data.decisionFactors} />}
				{data.eventHistory && <Field label="Event History" value={data.eventHistory} />}
				{data.reservationNotes && <Field label="Reservation Notes" value={data.reservationNotes} />}
				{!data.decisionFactors && !data.eventHistory && !data.reservationNotes && (
					<span className="rfp-review-empty">No considerations added.</span>
				)}
			</ReviewCard>
		);

		/* ── Additional Services ── */
		const servicesSection = (
			<ReviewCard title="Additional Services" stepIndex={6} onGoToStep={onGoToStep}>
				{arr(data.additionalServices) ? (
					<Chips items={data.additionalServices} />
				) : (
					<span className="rfp-review-empty">No additional services selected.</span>
				)}
			</ReviewCard>
		);

		/* ── Contract Signee ── */
		const signeeSection = (
			<ReviewCard title="Contract Signee" stepIndex={7} onGoToStep={onGoToStep}>
				<Field label="Name" value={[data.contractContactName].filter(Boolean).join(' ') || null} />
				<Field label="Company" value={data.contractCompanyName} />
				<Field label="Email" value={data.contractEmail} />
				<Field label="Phone" value={data.contractPhone} />
				<Field label="Signer Location" value={data.contractSignerLocation} />
			</ReviewCard>
		);

		/* ── Team Members ── */
		const selectedMemberIds = data.notifyTeamMembers || [];
		const selectedMembers = (teamMembers || []).filter(m => selectedMemberIds.includes(m._id));
		const teamMembersSection = (
			<ReviewCard title="Team Members" stepIndex={7} onGoToStep={onGoToStep}>
				{selectedMembers.length > 0 ? (
					selectedMembers.map((m) => (
						<div key={m._id} className="rfp-review-field">
							<span className="rfp-review-label">{m.firstName} {m.lastName}</span>
							<span className="rfp-review-value">{m.emailAddress}</span>
						</div>
					))
				) : (
					<span className="rfp-review-empty">No team members selected for notification.</span>
				)}
			</ReviewCard>
		);

		return (
			<div className="rfp-step-content">
				<h1 className="rfp-step-title">Review &amp; Submit</h1>
				<p className="rfp-step-sub">
					Everything looks good? Hit <strong>Submit RFP</strong> in the header or below. 
					Click <strong>Edit</strong> on any section to make changes.
				</p>

				<div className="rfp-review-grid">
					{eventSection}
					{locationSection}
					{datesSection}
					{agendaSection}
					{concessionsSection}
					{considerationsSection}
					{servicesSection}
					{signeeSection}
					{teamMembersSection}
				</div>
			</div>
		);
	}
}

export default ReviewSubmitStep;
